import momentTz from 'moment-timezone';

// Phase/tournament dates are stored as midnight UTC of the picked calendar
// date — they're really date-only values. Compare them as YYYY-MM-DD strings
// against "today" in the tournament's timezone so a Karachi user at 03:54
// May 7 reads May 7, not May 6.
const calendarDateUtc = (d) => (d ? momentTz.utc(d).format('YYYY-MM-DD') : null);
const todayInTz = (tz) => momentTz().tz(tz || 'UTC').format('YYYY-MM-DD');
const daysBetween = (fromYmd, toYmd) =>
  momentTz.utc(toYmd, 'YYYY-MM-DD').diff(momentTz.utc(fromYmd, 'YYYY-MM-DD'), 'days');

/**
 * Tournament lifecycle status (UPCOMING | IN_PROGRESS | COMPLETED), derived
 * from the tournament's matchSchedule.startDate / endDate compared to today's
 * calendar date in tournament.timezone.
 *
 * Returns null when matchSchedule.startDate is missing.
 */
export const computeTournamentStatus = (tournament) => {
  const start = tournament?.matchSchedule?.startDate;
  const end = tournament?.matchSchedule?.endDate;
  if (!start) return null;
  const today = todayInTz(tournament?.timezone);
  const startDay = calendarDateUtc(start);
  const endDay = calendarDateUtc(end);
  if (today < startDay) return 'UPCOMING';
  if (endDay && today > endDay) return 'COMPLETED';
  return 'IN_PROGRESS';
};

/**
 * "Current stage" across all categories — the active phase by name (with
 * overlapping date ranges unioned), evaluated against today in the
 * tournament's timezone.
 *
 *   - Today inside a stage's [start, end] → { stageName, currentDay, totalDays }
 *   - Today before all stages, or between two stages → { stageName: <next> } only
 *   - Today after every stage, or no phases → null
 */
export const computeCurrentStageProgress = (tournament) => {
  const today = todayInTz(tournament?.timezone);

  const grouped = new Map();
  for (const category of tournament?.categories || []) {
    for (const phase of category.phases || []) {
      const start = phase.matchSchedule?.startDate;
      const end = phase.matchSchedule?.endDate;
      if (!phase.name || !start || !end) continue;
      const startDay = calendarDateUtc(start);
      const endDay = calendarDateUtc(end);
      const existing = grouped.get(phase.name);
      if (!existing) {
        grouped.set(phase.name, { name: phase.name, startDay, endDay });
      } else {
        if (startDay < existing.startDay) existing.startDay = startDay;
        if (endDay > existing.endDay) existing.endDay = endDay;
      }
    }
  }

  const stages = [...grouped.values()].sort((a, b) => a.startDay.localeCompare(b.startDay));
  if (stages.length === 0) return null;

  const active = stages.find((s) => today >= s.startDay && today <= s.endDay);
  if (active) {
    const totalDays = daysBetween(active.startDay, active.endDay) + 1;
    const currentDay = Math.min(
      totalDays,
      Math.max(1, daysBetween(active.startDay, today) + 1)
    );
    return { stageName: active.name, currentDay, totalDays };
  }

  const nextUpcoming = stages.find((s) => s.startDay > today);
  if (nextUpcoming) return { stageName: nextUpcoming.name };

  return null;
};
